import { config } from "@noted/configuration";
import { NcLoadingIndicator } from "@noted/noted-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  HTMLAttributes,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAccount } from "~/components/account-context/account.context";
import { useAuth } from "~/components/auth-provider";
import { useFeatures } from "~/components/features.context";
import { useI18n } from "~/hooks/use-i18n";

const setDocumentTitle = (title: string | undefined) => {
  if (title) {
    window.document.title = title;
  }
};

const fragorRouteToKrios = (url = "") => url.replace("/org", "/n");

export interface LegacyPageRef {
  postMessage: (message: Record<string, unknown>, targetOrigin?: string) => void;
  iframe: HTMLIFrameElement | null;
}

export interface LegacyPageProps extends HTMLAttributes<HTMLIFrameElement> {
  /** Optional custom post message handler. Core message handling will always run first. */
  onMessage?: (event: MessageEvent) => void;
}

/** Iframe that loads routes in the legacy fragor app
 * These routes should all eventually be migrated to krios
 * All routes that are prefixed with /n/ krios loaded in this iframe as /org/
 * The iframe will send messages to the parent app (krios) to handle navigation, loading, etc.
 * The parent app (krios) will pass uer and organisation data to the iframe
 */
export const LegacyPage = forwardRef<LegacyPageRef, LegacyPageProps>((props, forwardedRef) => {
  const { onMessage, ...restProps } = props;
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { pathname, search } = useLocation();
  const url = `${pathname}${search}`;
  const [currentUrl, setCurrentUrl] = useState(url);
  const [iframeLoading, setIframeLoading] = useState(true);
  const navigate = useNavigate();
  const { account, organisation } = useAccount();
  const { availableFeatures } = useFeatures();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { getToken } = useAuth();

  const target = `${window?.location?.origin}/org`;

  // Expose iframe ref and postMessage method to parent components
  useImperativeHandle(
    forwardedRef,
    () => ({
      postMessage: (message: Record<string, unknown>, targetOrigin?: string) => {
        const origin = targetOrigin || window.location.origin;
        iframeRef.current?.contentWindow?.postMessage(message, origin);
      },
      get iframe() {
        return iframeRef.current;
      },
    }),
    []
  );

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      const { data } = event;
      const path = fragorRouteToKrios(data.path);
      if (data.type === "legacy-app:iframe-initialised") {
        const target = `${window.location.origin}`;
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: `parent-app:me-data`,
            organisation,
            user: account,
            features: availableFeatures,
          },
          target
        );
      }

      if (data.type === "legacy-app:iframe-loaded") {
        setIframeLoading(false);
        setDocumentTitle(data.documentTitle);
        return;
      }

      if (data.type === "legacy-app:iframe-navigate") {
        if (data.reloadParent) {
          queryClient.clear();
          navigate(path);
          return;
        }
        window.history.pushState({}, "", window.location.origin + path);
        setDocumentTitle(data.documentTitle);
        return;
      }

      if (data && data.type === "legacy-app:get-auth-token") {
        getToken().then(token => {
          if (!token || !data.requestId) {
            console.error("Error getting token cannot send data to iframe");
            return;
          }
          iframeRef.current?.contentWindow?.postMessage({
            type: "parent-app:auth-token",
            token,
            requestId: data.requestId,
          });
        });
      }

      if (data.type === "legacy-app:iframe-unauthenticated") {
        // We got a 401 in fragor, so we need to imitate what we do if we get a 401 in krios
        // (see handleNotAuthenticatedError in network-layer.tsx)
        // redirect to landing page
        window.location.href = config.landingUrl;
        return;
      }

      // Call custom message handler if provided (after core handling)
      onMessage?.(event);
    };
    window.addEventListener("message", handlePostMessage);

    return () => {
      window.removeEventListener("message", handlePostMessage);
    };
  }, [navigate, onMessage]);

  useEffect(() => {
    setCurrentUrl(url);
    setIframeLoading(true);
    setDocumentTitle(t("noted"));
  }, [url]);

  return (
    <div className="print:h-inherit block h-full max-h-full w-full overflow-hidden print:max-h-[unset] print:min-h-full print:overflow-visible">
      {iframeLoading && <NcLoadingIndicator variant="logo" />}
      {useMemo(
        () => (
          <iframe
            ref={iframeRef}
            title="legacy page"
            src={new URL(currentUrl.replace("n", "org"), target).toString()}
            key={currentUrl}
            className="print:h-inherit print:overflow-clip-margin [overflow-clip-margin]:initial mb-[-0.2rem] h-full w-full print:min-h-full print:overflow-visible"
            {...restProps}
          ></iframe>
        ),
        [currentUrl, restProps]
      )}
    </div>
  );
});

LegacyPage.displayName = "LegacyPage";
